/**
 * Stage 2a: SERP Analysis — context-based version using StagePanel.
 *
 * Shows the primary keyword with rationale, allows changing it,
 * accepts SERP CSV + Google results, analyzes competitive landscape.
 */

import React, { useCallback, useState } from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import { useCsvUpload } from '../shared/useCsvUpload';
import { KeywordSelector } from '../shared/KeywordSelector';
import type { ArticleDocument } from '../types';

function renderOutput(doc: ArticleDocument) {
  const serp = doc.targeting?.serp;
  if (!serp?.overview) {
    return <div className="text-sm text-gray-500">No SERP analysis yet.</div>;
  }
  const o = serp.overview;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-gray-700">SERP Decision</div>
        <span
          className={`text-sm font-semibold px-3 py-1 rounded-full ${
            o.decision === 'accept'
              ? 'bg-green-100 text-green-700'
              : o.decision === 'flag'
              ? 'bg-amber-100 text-amber-700'
              : 'bg-red-100 text-red-700'
          }`}
        >
          {o.decision}
        </span>
      </div>
      {o.decision_rationale && <p className="text-sm text-gray-600">{o.decision_rationale}</p>}
      {o.dominant_content_format && (
        <p className="text-xs text-gray-500">
          <span className="text-gray-400">Dominant format:</span> {o.dominant_content_format}
        </p>
      )}
      {serp.paa_questions && serp.paa_questions.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-700">People Also Ask</div>
          <ul className="text-xs text-gray-600 list-disc pl-4">
            {serp.paa_questions.map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        </div>
      )}
      {doc.targeting?.competitors?.urls && doc.targeting.competitors.urls.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-700">Competitor URLs ({doc.targeting.competitors.urls.length})</div>
          {doc.targeting.competitors.urls.map((u, i) => (
            <div key={i} className="text-xs text-gray-600">#{u.position} {u.domain}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Stage2aSerpV2() {
  const { document } = useArticleContext();
  const kw = document?.targeting?.keywords;
  const primaryKeyword = kw?.primary?.keyword;
  const hasPrimary = Boolean(primaryKeyword);
  const candidates = kw?.primary_candidates || [];

  const serpFilter = useCallback((rows: Record<string, string>[]) => {
    return rows.filter(row => {
      const t = String(row.Type || '').trim();
      return t === 'Organic' || t === 'AI Overview';
    }).slice(0, 20);
  }, []);
  const { rawCsv, fileName, fileRef, handleFileUpload } = useCsvUpload({ filterRows: serpFilter });
  const [googleResults, setGoogleResults] = useState('');

  const buildPayload = useCallback(() => ({
    semrush_serp_csv: rawCsv,
    google_results: googleResults,
  }), [rawCsv, googleResults]);

  // Build keyword options from primary_candidates
  const keywordOptions = candidates.map((c: any) => ({
    keyword: typeof c === 'string' ? c : c?.keyword || '',
    volume: typeof c === 'object' ? c?.volume : null,
    kd: typeof c === 'object' ? c?.kd : null,
  })).filter((o: any) => o.keyword);

  return (
    <StagePanel
      stageId="stage_2a_serp_overview"
      isLlmStage
      title="Stage 2a: SERP Analysis"
      description="Analyze the SERP landscape for your primary keyword."
      runDisabled={!hasPrimary || !rawCsv.trim() || !googleResults.trim()}
      runDisabledReason={!hasPrimary ? 'Requires primary keyword from Stage 1b' : 'Provide SERP CSV and Google results'}
      runLabel="Analyze SERP →"
      renderOutput={renderOutput}
      buildPayload={buildPayload}
    >
      <div className="space-y-4">
        {/* Primary keyword selector */}
        <KeywordSelector
          options={keywordOptions}
          seoSignal={kw?.keyword_decision?.seo_signal}
        />

        {/* SERP data input */}
        <div className="border border-gray-200 rounded-lg p-4 space-y-4">
          <div>
            <div className="text-sm font-medium text-gray-700">Provide SERP data</div>
            <p className="text-xs text-gray-500 mt-1">
              Search for <strong>'{primaryKeyword || '...'}'</strong> in SEMrush and Google (incognito window) and provide the results below.
            </p>
          </div>

          <div>
            <div className="text-xs font-medium text-gray-700 mb-1.5">SEMrush SERP CSV</div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFileUpload} />
            <button
              type="button"
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
              onClick={() => fileRef.current?.click()}
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
              </svg>
              {fileName || 'Choose CSV file'}
            </button>
            {fileName && <span className="text-xs text-green-600 ml-2">{fileName}</span>}
          </div>

          <div>
            <div className="text-xs font-medium text-gray-700 mb-1.5">Google Results</div>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              rows={5}
              value={googleResults}
              onChange={(e) => setGoogleResults(e.target.value)}
              placeholder="Paste Google SERP results here..."
            />
          </div>
        </div>
      </div>
    </StagePanel>
  );
}
